import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { startCase } from 'lodash';
import PropTypes from 'prop-types';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import { NavItem } from '@agconnections/grow-ui';
import { useLocation, NavLink } from 'react-router-dom';

import ExpandLessIcon from 'assets/expand_less.svg?react';
import ExpandMoreIcon from 'assets/expand_more.svg?react';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import ClickableDiv from 'components/ClickableDiv';
import getIsNavItemActive from '../../helpers/helpers';

const CollapsibleNavItem = ({ item, isOpened }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isCollapsedIconHovered, setIsCollapsedIconHovered] = useState(false);
  const [showSidenav, setShowSidenav] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const amplitude = useContext(AmplitudeContext);
  const location = useLocation();
  const buttonRef = useRef(null);
  const sidenavRef = useRef(null);

  useEffect(() => {
    if (isOpened) {
      setShowSidenav(false);
    } else {
      setIsCollapsed(true);
    }
  }, [isOpened]);

  const isActive = useMemo(
    () => getIsNavItemActive({ item, pathname: location.pathname }),
    [item, location.pathname]
  );

  const renderNavItem = ({ route, pathname, subMenu = false }) => (
    <NavItem
      key={route.key}
      disabled={route.disabled}
      icon={subMenu ? null : route.icon}
      subMenu={subMenu}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      active={getIsNavItemActive({
        item: route,
        pathname
      })}
      open
      as={
        <NavLink
          key={route.key}
          onClick={() => {
            amplitude.sendEventToAmplitude(amplitude.filterNavItems(route.key));
            if (subMenu) {
              setIsHovered(false);
              setShowSidenav(false);
            }
          }}
          to={route.path}
          sensitive
          secure
          exact
        />
      }
    >
      <>{startCase(route.id)}</>
    </NavItem>
  );

  const ParentIcon = item.icon;

  useEffect(() => {
    if (!showSidenav) return () => {};
    const handleClickOutside = event => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        sidenavRef.current &&
        !sidenavRef.current.contains(event.target)
      ) {
        setShowSidenav(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSidenav]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`focus:outline-hidden relative gr-flex gr-flex-row gr-items-center  gr-w-64 gr-leading-5 gr-py-1 gr-relative gr-items-center gr-group gr-text-neutral-500 hover:gr-text-white cursor-pointer ${
          isCollapsed && isActive ? 'gr-bg-neutral-1000' : ''
        }`}
        onClick={() =>
          isOpened ? setIsCollapsed(!isCollapsed) : setShowSidenav(!showSidenav)
        }
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Tooltip
          placement="right"
          title={startCase(item.id)}
          color="#232630"
          open={!isOpened && !showSidenav && isCollapsedIconHovered}
        >
          <ClickableDiv
            onMouseEnter={() => setIsCollapsedIconHovered(true)}
            onMouseLeave={() => setIsCollapsedIconHovered(false)}
            className={`gr-h-8 gr-items-center gr-justify-center gr-flex gr-w-16 ${
              isActive || isHovered ? 'text-white' : ''
            }`}
          >
            <span
              className={`${
                isCollapsed && isActive ? 'gr-bg-white' : 'gr-bg-inherit'
              } gr-h-8 gr-absolute gr-rounded-r gr-w-1 gr-flex-1 gr-left-0`}
            />
            <ParentIcon fill={isActive || isHovered ? 'white' : '#696F88'} />
          </ClickableDiv>
        </Tooltip>
        <div className="flex-1 text-left">
          <span
            className={`gr-opacity-100 gr-transition gr-ease-in gr-duration-100 gr-text-sm gr-min-w-full ${
              isActive || isHovered ? 'text-white' : ''
            }`}
          >
            {startCase(item.id)}
          </span>
        </div>

        <div className="gr-h-8 gr-items-center gr-justify-center gr-flex gr-w-16">
          {item?.subRoutes?.length > 0 &&
            (isCollapsed ? (
              <ExpandMoreIcon
                width={18}
                height={18}
                fill={isHovered ? 'white' : '#A3A9B9'}
                aria-label="expand-more-icon"
              />
            ) : (
              <ExpandLessIcon
                width={18}
                height={18}
                fill={isHovered ? 'white' : '#A3A9B9'}
                aria-label="expand-less-icon"
              />
            ))}
        </div>
      </button>
      {/* Render floating sidenav as sibling, not child, for robust outside click handling */}
      {showSidenav && (
        <div
          ref={sidenavRef}
          className="absolute z-50 py-2 bg-neutral-90 w-183px overflow-hidden"
          style={{ left: '68px', marginTop: -40 }}
        >
          {item?.subRoutes?.map(route =>
            renderNavItem({
              route,
              pathname: location.pathname,
              subMenu: true
            })
          )}
        </div>
      )}
      <div>
        {!isCollapsed &&
          item?.subRoutes?.map(route =>
            renderNavItem({ route, pathname: location.pathname })
          )}
      </div>
    </>
  );
};

CollapsibleNavItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    key: PropTypes.string,
    path: PropTypes.string,
    icon: PropTypes.node,
    subRoutes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        key: PropTypes.string,
        path: PropTypes.string
      })
    )
  }).isRequired,
  isOpened: PropTypes.bool.isRequired
};

export default CollapsibleNavItem;
